import BadgeIcon from "@mui/icons-material/Badge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EmailIcon from "@mui/icons-material/Email";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import InfoIcon from "@mui/icons-material/Info";
import RvHookupIcon from "@mui/icons-material/RvHookup";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Alert, Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import React from "react";
import { AuditLogLink } from "../../api/auditApi";
import { DispatcherDto, useGetDispatchersQuery, useGetEmployeeByDriverInfoIdQuery } from "../../api/employeesApi";
import { Trailer, useGetTrailerByIdQuery } from "../../api/trailersApi";
import { Vehicle, useGetVehicleByIdQuery } from "../../api/vehiclesApi";

export const formatVehicleDisplayLabel = (vehicle?: Vehicle | null) => {
  if (!vehicle) {
    return undefined;
  }

  if (vehicle.name) {
    return vehicle.name;
  }

  if (vehicle.registrationNumber) {
    return vehicle.registrationNumber;
  }

  return `Vehicle #${vehicle.id}`;
};

export const formatTrailerDisplayLabel = (trailer?: Trailer | null) => {
  if (!trailer) {
    return undefined;
  }

  return trailer.unitNumber ?? `Trailer #${trailer.id}`;
};

export const formatDispatcherDisplayLabel = (dispatcher?: DispatcherDto | null) => {
  if (!dispatcher) {
    return undefined;
  }

  const fullName = [dispatcher.firstName, dispatcher.lastName].filter(Boolean).join(" ").trim();
  return fullName || dispatcher.email || `Dispatcher #${dispatcher.id}`;
};

export type DriverHistoryTab = "vehicle" | "trailer" | "dispatcher";

type EntityCardVariant = "default" | "overlay";

interface EntityCardProps {
  entityId: number;
  role?: string | null;
  open?: boolean;
  compact?: boolean;
  variant?: EntityCardVariant;
}

interface AuditEntityConfig {
  label: string;
  icon: React.ReactElement;
  driverTab?: DriverHistoryTab;
  DetailCard?: React.ComponentType<EntityCardProps>;
  SummaryCard?: React.ComponentType<EntityCardProps>;
}

const InlineInfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
}> = ({ icon, label, value }) => (
  <Box sx={{ alignItems: "center", display: "flex", flexShrink: 0, gap: 0.5, minWidth: 0 }}>
    <Box sx={{ color: "text.secondary", display: "flex", flexShrink: 0 }}>{icon}</Box>
    <Typography color="text.secondary" sx={{ fontSize: "0.65rem", lineHeight: 1.2, whiteSpace: "nowrap" }}>
      {label}:
    </Typography>
    <Typography
      noWrap
      sx={{
        fontSize: "0.75rem",
        fontWeight: 600,
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value || "-"}
    </Typography>
  </Box>
);

const CompactInfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
}> = ({ icon, label, value }) => (
  <Box sx={{ alignItems: "center", display: "flex", gap: 1, minWidth: 0 }}>
    <Box sx={{ color: "text.secondary", display: "flex", flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography color="text.secondary" sx={{ fontSize: "0.7rem", lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  </Box>
);

const FieldRow: React.FC<{ label: string; value?: React.ReactNode; icon?: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1, py: 0.5 }}>
    {icon && <Box sx={{ color: "text.secondary", display: "flex", pt: "2px" }}>{icon}</Box>}
    <Typography color="text.secondary" sx={{ minWidth: icon ? 104 : 120, fontSize: "0.8125rem" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.8125rem", wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

const SummaryCardShell: React.FC<{
  title: string;
  icon: React.ReactNode;
  isLoading?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, isLoading, children }) => (
  <Card variant="outlined">
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Box sx={{ alignItems: "center", display: "flex", gap: 1, mb: 1.25 }}>
        {icon}
        <Typography variant="subtitle2">{title}</Typography>
      </Box>
      {isLoading ? <CircularProgress size={24} /> : children}
    </CardContent>
  </Card>
);

const DetailCardShell: React.FC<{
  title: string;
  icon: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, isLoading, isError, children }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Box sx={{ alignItems: "center", display: "flex", gap: 1, mb: 1.5 }}>
        <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
        <Typography variant="subtitle2">{title}</Typography>
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Alert severity="warning">Details could not be loaded.</Alert>
      ) : (
        children
      )}
    </CardContent>
  </Card>
);

const VehicleSummaryCard: React.FC<EntityCardProps> = ({ entityId, open = true }) => {
  const vehicleQuery = useGetVehicleByIdQuery(entityId, { skip: !open || !entityId });
  const vehicle = vehicleQuery.data;

  return (
    <SummaryCardShell
      title="Vehicle Details"
      icon={<LocalShippingIcon color="primary" fontSize="small" />}
      isLoading={vehicleQuery.isFetching && !vehicle}
    >
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" } }}>
        <CompactInfoItem icon={<LocalShippingIcon fontSize="small" />} label="Name" value={vehicle?.name} />
        <CompactInfoItem icon={<FingerprintIcon fontSize="small" />} label="VIN" value={vehicle?.vin} />
        <CompactInfoItem icon={<BadgeIcon fontSize="small" />} label="Registration" value={vehicle?.registrationNumber} />
        <CompactInfoItem icon={<InfoIcon fontSize="small" />} label="Make / Model" value={[vehicle?.make, vehicle?.model].filter(Boolean).join(" ")} />
        <CompactInfoItem icon={<BadgeIcon fontSize="small" />} label="Year" value={vehicle?.year} />
        <CompactInfoItem icon={<CheckCircleIcon fontSize="small" color={vehicle?.lastStatus ? "success" : "disabled"} />} label="Status" value={vehicle?.lastStatus} />
      </Box>
    </SummaryCardShell>
  );
};

const VehicleDetailCard: React.FC<EntityCardProps> = ({ entityId, open = true }) => {
  const vehicleQuery = useGetVehicleByIdQuery(entityId, { skip: !open || !entityId });
  const vehicle = vehicleQuery.data;

  return (
    <DetailCardShell
      title={formatVehicleDisplayLabel(vehicle) ?? `Truck #${entityId}`}
      icon={<LocalShippingIcon fontSize="small" />}
      isLoading={vehicleQuery.isFetching}
      isError={vehicleQuery.isError}
    >
      <FieldRow icon={<LocalShippingIcon fontSize="small" />} label="Name" value={vehicleQuery.data?.name} />
      <FieldRow icon={<FingerprintIcon fontSize="small" />} label="VIN" value={vehicleQuery.data?.vin} />
      <FieldRow icon={<BadgeIcon fontSize="small" />} label="Registration" value={vehicleQuery.data?.registrationNumber} />
      <FieldRow icon={<InfoIcon fontSize="small" />} label="Make / Model" value={[vehicleQuery.data?.make, vehicleQuery.data?.model].filter(Boolean).join(" ")} />
      <FieldRow icon={<BadgeIcon fontSize="small" />} label="Year" value={vehicleQuery.data?.year} />
      <FieldRow icon={<CheckCircleIcon fontSize="small" />} label="Status" value={vehicleQuery.data?.lastStatus} />
    </DetailCardShell>
  );
};

const TrailerSummaryCard: React.FC<EntityCardProps> = ({ entityId, open = true }) => {
  const trailerQuery = useGetTrailerByIdQuery(entityId, { skip: !open || !entityId });
  const trailer = trailerQuery.data;

  return (
    <SummaryCardShell
      title="Trailer Details"
      icon={<LocalShippingIcon color="primary" fontSize="small" />}
      isLoading={trailerQuery.isFetching && !trailer}
    >
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" } }}>
        <CompactInfoItem icon={<BadgeIcon fontSize="small" />} label="Unit Number" value={trailer?.unitNumber} />
        <CompactInfoItem icon={<FingerprintIcon fontSize="small" />} label="VIN" value={trailer?.vin} />
        <CompactInfoItem icon={<LocalShippingIcon fontSize="small" />} label="Type" value={trailer?.trailerType} />
        <CompactInfoItem icon={<InfoIcon fontSize="small" />} label="Make / Model" value={[trailer?.make, trailer?.model].filter(Boolean).join(" ")} />
        <CompactInfoItem icon={<BadgeIcon fontSize="small" />} label="Year" value={trailer?.year} />
        <CompactInfoItem icon={<CheckCircleIcon fontSize="small" color={trailer?.trailerStatus ? "success" : "disabled"} />} label="Status" value={trailer?.trailerStatus} />
      </Box>
    </SummaryCardShell>
  );
};

const TrailerDetailCard: React.FC<EntityCardProps> = ({ entityId, open = true }) => {
  const trailerQuery = useGetTrailerByIdQuery(entityId, { skip: !open || !entityId });
  const trailer = trailerQuery.data;

  return (
    <DetailCardShell
      title={formatTrailerDisplayLabel(trailer) ?? `Trailer #${entityId}`}
      icon={<RvHookupIcon fontSize="small" />}
      isLoading={trailerQuery.isFetching}
      isError={trailerQuery.isError}
    >
      <FieldRow icon={<BadgeIcon fontSize="small" />} label="Unit Number" value={trailerQuery.data?.unitNumber} />
      <FieldRow icon={<FingerprintIcon fontSize="small" />} label="VIN" value={trailerQuery.data?.vin} />
      <FieldRow icon={<LocalShippingIcon fontSize="small" />} label="Type" value={trailerQuery.data?.trailerType} />
      <FieldRow icon={<InfoIcon fontSize="small" />} label="Make / Model" value={[trailerQuery.data?.make, trailerQuery.data?.model].filter(Boolean).join(" ")} />
      <FieldRow icon={<BadgeIcon fontSize="small" />} label="Year" value={trailerQuery.data?.year} />
      <FieldRow icon={<CheckCircleIcon fontSize="small" />} label="Status" value={trailerQuery.data?.trailerStatus} />
    </DetailCardShell>
  );
};

const DriverSummaryCard: React.FC<EntityCardProps> = ({ entityId, open = true, variant = "default" }) => {
  const driverQuery = useGetEmployeeByDriverInfoIdQuery(entityId, { skip: !open || !entityId });
  const driver = driverQuery.data;

  if (variant === "overlay") {
    if (driverQuery.isFetching && !driver) {
      return (
        <Box sx={{ alignItems: "center", display: "flex", gap: 1, px: 1.25, py: 0.75 }}>
          <CircularProgress size={18} />
          <Typography color="text.secondary" sx={{ fontSize: "0.75rem" }}>
            Loading driver...
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: 1.25,
          px: 1.25,
          py: 0.75,
        }}
      >
        <Box sx={{ alignItems: "center", display: "flex", flexShrink: 0, gap: 0.5 }}>
          <PersonIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap" }}>
            Driver Details
          </Typography>
        </Box>
        <InlineInfoItem
          icon={<PersonIcon sx={{ fontSize: 14 }} />}
          label="Name"
          value={[driver?.firstName, driver?.lastName].filter(Boolean).join(" ")}
        />
        <InlineInfoItem icon={<EmailIcon sx={{ fontSize: 14 }} />} label="Email" value={driver?.email} />
        <InlineInfoItem icon={<PhoneIcon sx={{ fontSize: 14 }} />} label="Phone" value={driver?.phoneNumber} />
        <InlineInfoItem icon={<BadgeIcon sx={{ fontSize: 14 }} />} label="Employee ID" value={driver?.id} />
        <InlineInfoItem
          icon={
            <CheckCircleIcon
              sx={{ fontSize: 14 }}
              color={driver?.isActive ? "success" : "disabled"}
            />
          }
          label="Active"
          value={driver ? (driver.isActive ? "Yes" : "No") : undefined}
        />
      </Box>
    );
  }

  return (
    <SummaryCardShell
      title="Driver Details"
      icon={<PersonIcon color="primary" fontSize="small" />}
      isLoading={driverQuery.isFetching && !driver}
    >
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" } }}>
        <CompactInfoItem icon={<PersonIcon fontSize="small" />} label="Name" value={[driver?.firstName, driver?.lastName].filter(Boolean).join(" ")} />
        <CompactInfoItem icon={<EmailIcon fontSize="small" />} label="Email" value={driver?.email} />
        <CompactInfoItem icon={<PhoneIcon fontSize="small" />} label="Phone" value={driver?.phoneNumber} />
        <CompactInfoItem icon={<BadgeIcon fontSize="small" />} label="Employee ID" value={driver?.id} />
        <CompactInfoItem icon={<PersonIcon fontSize="small" />} label="User ID" value={driver?.userId} />
        <CompactInfoItem icon={<CheckCircleIcon fontSize="small" color={driver?.isActive ? "success" : "disabled"} />} label="Active" value={driver ? (driver.isActive ? "Yes" : "No") : undefined} />
      </Box>
    </SummaryCardShell>
  );
};

const DriverDetailCard: React.FC<EntityCardProps> = ({ entityId, open = true }) => {
  const driverQuery = useGetEmployeeByDriverInfoIdQuery(entityId, { skip: !open || !entityId });
  const driver = driverQuery.data;
  const driverName = [driver?.firstName, driver?.lastName].filter(Boolean).join(" ");

  return (
    <DetailCardShell
      title={driverName || `Driver Info #${entityId}`}
      icon={<PersonIcon fontSize="small" />}
      isLoading={driverQuery.isFetching}
      isError={driverQuery.isError}
    >
      <FieldRow icon={<PersonIcon fontSize="small" />} label="Name" value={[driverQuery.data?.firstName, driverQuery.data?.lastName].filter(Boolean).join(" ")} />
      <FieldRow icon={<EmailIcon fontSize="small" />} label="Email" value={driverQuery.data?.email} />
      <FieldRow icon={<PhoneIcon fontSize="small" />} label="Phone" value={driverQuery.data?.phoneNumber} />
      <FieldRow icon={<BadgeIcon fontSize="small" />} label="Employee ID" value={driverQuery.data?.id} />
      <FieldRow icon={<PersonIcon fontSize="small" />} label="User ID" value={driverQuery.data?.userId} />
      <FieldRow icon={<CheckCircleIcon fontSize="small" color={driverQuery.data?.isActive ? "success" : "disabled"} />} label="Active" value={driverQuery.data ? (driverQuery.data.isActive ? "Yes" : "No") : undefined} />
    </DetailCardShell>
  );
};

const DispatcherDetailCard: React.FC<EntityCardProps> = ({ entityId, open = true }) => {
  const dispatchersQuery = useGetDispatchersQuery(undefined, { skip: !open || !entityId });
  const dispatcher = dispatchersQuery.data?.find((item) => item.id === entityId);

  return (
    <DetailCardShell
      title={formatDispatcherDisplayLabel(dispatcher) ?? `Dispatcher #${entityId}`}
      icon={<SupportAgentIcon fontSize="small" />}
      isLoading={dispatchersQuery.isFetching && !dispatcher}
      isError={dispatchersQuery.isError}
    >
      <FieldRow
        icon={<PersonIcon fontSize="small" />}
        label="Name"
        value={[dispatcher?.firstName, dispatcher?.lastName].filter(Boolean).join(" ")}
      />
      <FieldRow icon={<EmailIcon fontSize="small" />} label="Email" value={dispatcher?.email} />
      <FieldRow icon={<BadgeIcon fontSize="small" />} label="Employee ID" value={dispatcher?.id} />
      <FieldRow
        icon={<CheckCircleIcon fontSize="small" color={dispatcher?.isActive ? "success" : "disabled"} />}
        label="Active"
        value={dispatcher ? (dispatcher.isActive ? "Yes" : "No") : undefined}
      />
    </DetailCardShell>
  );
};

const GenericDetailCard: React.FC<EntityCardProps & { entityType?: string }> = ({
  entityId,
  entityType = "Entity",
  role,
}) => (
  <DetailCardShell title={`${entityType} #${entityId}`} icon={<InfoIcon fontSize="small" />}>
    <FieldRow icon={<InfoIcon fontSize="small" />} label="Type" value={entityType} />
    <FieldRow icon={<BadgeIcon fontSize="small" />} label="ID" value={entityId} />
    <FieldRow icon={<InfoIcon fontSize="small" />} label="Role" value={role} />
  </DetailCardShell>
);

export const AUDIT_ENTITY_REGISTRY: Record<string, AuditEntityConfig> = {
  Vehicle: {
    label: "Vehicle",
    icon: <LocalShippingIcon fontSize="small" />,
    SummaryCard: VehicleSummaryCard,
    DetailCard: VehicleDetailCard,
  },
  Trailer: {
    label: "Trailer",
    icon: <LocalShippingIcon fontSize="small" />,
    SummaryCard: TrailerSummaryCard,
    DetailCard: TrailerDetailCard,
  },
  EmployeeDriverInfo: {
    label: "Driver",
    icon: <PersonIcon fontSize="small" />,
    SummaryCard: DriverSummaryCard,
    DetailCard: DriverDetailCard,
  },
  Employee: {
    label: "Employee",
    icon: <PersonIcon fontSize="small" />,
  },
  VehicleAssignment: {
    label: "Vehicle Assignment",
    icon: <LocalShippingIcon fontSize="small" />,
    driverTab: "vehicle",
  },
  TrailerAssignment: {
    label: "Trailer Assignment",
    icon: <RvHookupIcon fontSize="small" />,
    driverTab: "trailer",
  },
  DriverDispatcherAssignment: {
    label: "Dispatcher Assignment",
    icon: <SupportAgentIcon fontSize="small" />,
    driverTab: "dispatcher",
  },
};

export const getAuditEntityConfig = (entityType?: string | null) =>
  entityType ? AUDIT_ENTITY_REGISTRY[entityType] : undefined;

export const getDriverHistoryTabForAudit = (entityType: string): DriverHistoryTab | null =>
  AUDIT_ENTITY_REGISTRY[entityType]?.driverTab ?? null;

export const getDriverHistoryTabConfigs = () => [
  {
    value: "vehicle" as DriverHistoryTab,
    label: "Vehicles",
    icon: AUDIT_ENTITY_REGISTRY.VehicleAssignment.icon,
  },
  {
    value: "trailer" as DriverHistoryTab,
    label: "Trailers",
    icon: AUDIT_ENTITY_REGISTRY.TrailerAssignment.icon,
  },
  {
    value: "dispatcher" as DriverHistoryTab,
    label: "Dispatchers",
    icon: AUDIT_ENTITY_REGISTRY.DriverDispatcherAssignment.icon,
  },
];

export const AuditEntitySummaryCard: React.FC<EntityCardProps & { entityType: string }> = ({
  entityType,
  entityId,
  open,
  variant,
}) => {
  const SummaryCard = getAuditEntityConfig(entityType)?.SummaryCard;
  return SummaryCard ? <SummaryCard entityId={entityId} open={open} variant={variant} /> : null;
};

export const AuditEntityDetailCard: React.FC<{
  link: Pick<AuditLogLink, "entityType" | "entityId" | "role">;
  open?: boolean;
  compact?: boolean;
}> = ({ link, open, compact }) => {
  const config = getAuditEntityConfig(link.entityType);

  if (link.entityType === "Employee" && link.role === "Dispatcher") {
    return <DispatcherDetailCard entityId={link.entityId} open={open} compact={compact} />;
  }

  const DetailCard = config?.DetailCard;

  if (!DetailCard) {
    return (
      <GenericDetailCard
        entityType={config?.label ?? link.entityType}
        entityId={link.entityId}
        role={link.role}
        open={open}
        compact={compact}
      />
    );
  }

  return <DetailCard entityId={link.entityId} role={link.role} open={open} compact={compact} />;
};
